import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './HowItWorksModal.css';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenLogin?: () => void;
}

const HowItWorksModal: React.FC<HowItWorksModalProps> = ({ isOpen, onClose, onOpenLogin }) => {
  const { isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "$TPC",
      content: "For early users, we will be using $TPC to place parlays. View $TPC as free play. $TPC can be earned upon account creation (see your balance) and by referring friends using your referral code (referee must place a parlay for you to earn $TPC). There are no deposits necessary. At a later stage, USDC will be sent to your wallet based on your $TPC balance."
    },
    {
      title: "Build your parlay",
      content: "Buy 'Yes' or 'No' shares depending on your prediction. These shares will either resolve to $1 or $0 depending on the outcome of the market. Add more than one market to form a parlay."
    },
    {
      title: "Add a stake",
      content: "Once you've added your markets, add a stake and view the parlay quote."
    },
    {
      title: "Place the parlay",
      content: "Review the quote and place the parlay. The quote expires 2 minutes after creation."
    },
    {
      title: "Profit",
      content: "If your parlay wins, you will receive your expected payout. You can also cash out your parlay at any time."
    }
  ];

  // Reset to first step when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGetStarted = () => {
    if (!isAuthenticated && onOpenLogin) {
      onClose(); // Close the how it works modal first
      onOpenLogin(); // Then open the login modal
    } else {
      // User is already authenticated, just close the modal
      console.log('User is already authenticated');
      onClose();
    }
  };

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="how-it-works-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="how-it-works-modal-content">
        <button 
          className="close-how-it-works-modal-btn"
          onClick={onClose}
        >
          ×
        </button>
        
        <div className="modal-body">
          <div className="how-it-works-step">
            <h2 className="step-title">{currentStepData.title}</h2>
            <p className="step-content">{currentStepData.content}</p>
          </div>
        </div>

        <div className="step-navigation">
          {currentStep === 0 ? (
            <button className="next-step-btn" onClick={handleNext}>
              Next
            </button>
          ) : isLastStep ? (
            <button className="get-started-btn" onClick={handleGetStarted}>
              Get Started
            </button>
          ) : (
            <>
              <button className="back-step-btn" onClick={handlePrevious}>
                Back
              </button>
              <button className="next-step-btn" onClick={handleNext}>
                Next
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HowItWorksModal;